import React, { useState } from 'react';
import { Calendar as CalendarIcon, Clock, User, PawPrint, MapPin, Phone, Mail, MessageSquare, Calculator, Euro } from 'lucide-react';
import { format } from 'date-fns';
import { de } from 'date-fns/locale';
import axios from 'axios';
import { Calendar } from './ui/calendar';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Switch } from './ui/switch';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { toast } from 'sonner';
import { availableTimeSlots, petTypes, destinations } from '../mock';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Booking = () => {
  const [selectedDate, setSelectedDate] = useState();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    petType: '',
    petName: '',
    petSize: '',
    pickupAddress: '',
    destination: '',
    destinationType: '',
    tripType: 'short',
    roundTrip: false,
    waitingService: false,
    waitingServiceDuration: 0,
    isAnimalWelfare: false,
    animalWelfareOrg: '',
    timeSlot: '',
    notes: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [estimatedPrice, setEstimatedPrice] = useState(null);
  const [isCalculatingPrice, setIsCalculatingPrice] = useState(false);
  const [priceError, setPriceError] = useState('');

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Preis bei jeder Änderung relevanter Felder zurücksetzen
    if (['pickupAddress', 'destination', 'tripType', 'roundTrip', 'waitingService', 'waitingServiceDuration', 'isAnimalWelfare'].includes(field)) {
      setEstimatedPrice(null);
      setPriceError('');
    }
  };

  const handleCalculatePrice = async () => {
    if (!formData.pickupAddress || !formData.destination) {
      toast.error('Bitte Abhol- und Zieladresse eingeben');
      return;
    }
    setIsCalculatingPrice(true);
    setPriceError('');
    try {
      const res = await axios.post(`${API}/calculator/calculate-price-advanced`, {
        pickupAddress: formData.pickupAddress,
        destination: formData.destination,
        tripType: formData.tripType,
        isWithinHagen: true,
        isSenior: false,
        roundTrip: formData.roundTrip,
        waitingService: formData.waitingService,
        waitingServiceDuration: formData.waitingServiceDuration || 0,
        infectiousAnimal: false,
        isAnimalWelfare: formData.tripType === 'long' && formData.isAnimalWelfare
      });
      setEstimatedPrice(res.data);
    } catch (err) {
      console.error('Preisberechnung fehlgeschlagen:', err);
      setPriceError('Preis konnte nicht berechnet werden. Bitte Adressen prüfen.');
    } finally {
      setIsCalculatingPrice(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!selectedDate || !formData.timeSlot) {
      toast.error('Bitte wählen Sie Datum und Uhrzeit aus');
      return;
    }

    if (!acceptedTerms) {
      toast.error('Bitte akzeptieren Sie die AGB und Datenschutzerklärung');
      return;
    }

    if (formData.tripType === 'long' && formData.isAnimalWelfare && !formData.animalWelfareOrg.trim()) {
      toast.error('Bitte den Namen des Tierschutzvereins eingeben');
      return;
    }

    setIsSubmitting(true);
    
    try {
      const bookingData = {
        ...formData,
        date: format(selectedDate, 'yyyy-MM-dd')
      };
      
      const response = await axios.post(`${API}/bookings`, bookingData);
      
      if (response.data) {
        toast.success('Buchung erfolgreich!', {
          description: `Buchungsnummer: ${response.data.id}`
        });
        
        // Reset form
        setFormData({
          name: '',
          email: '',
          phone: '',
          petType: '',
          petName: '',
          petSize: '',
          pickupAddress: '',
          destination: '',
          destinationType: '',
          tripType: 'short',
          roundTrip: false,
          waitingService: false,
          waitingServiceDuration: 0,
          isAnimalWelfare: false,
          animalWelfareOrg: '',
          timeSlot: '',
          notes: ''
        });
        setSelectedDate(undefined);
        setAcceptedTerms(false);
      }
    } catch (error) {
      console.error('Buchungsfehler:', error);
      toast.error('Buchung fehlgeschlagen', {
        description: 'Bitte versuchen Sie es erneut oder rufen Sie uns an.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const selectedPetType = petTypes.find(pt => pt.value === formData.petType);

  return (
    <section id="booking" className="py-20 bg-white">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-4xl sm:text-5xl font-bold mb-4">
            <span className="bg-gradient-to-r from-blue-600 to-orange-500 bg-clip-text text-transparent">
              Termin buchen
            </span>
          </h2>
          <p className="text-xl text-gray-600">
            Buchen Sie Ihren Transport schnell und einfach online
          </p>
        </div>

        {/* Info-Box: Terminplanungsservice */}
        <div className="mb-8 bg-gradient-to-r from-blue-50 to-orange-50 border border-blue-200 rounded-xl p-5 shadow-sm">
          <div className="flex items-start gap-4">
            <div className="bg-blue-100 rounded-full p-2 mt-1">
              <CalendarIcon className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <h3 className="font-semibold text-blue-900 mb-1">📅 Kostenloser Terminplanungsservice</h3>
              <p className="text-gray-700 text-sm leading-relaxed">
                Ihr Wunschtermin ist bereits vergeben? Kein Problem! Gerne übernehme ich die Terminabstimmung 
                mit Ihrer Tierarztpraxis für Sie – <strong>kostenlos</strong>. Informieren Sie einfach Ihren 
                Tierarzt kurz telefonisch, dass ich zur Terminplanung berechtigt bin, und ich kümmere mich 
                um einen passenden Termin für Sie und Ihr Tier.
              </p>
            </div>
          </div>
        </div>

        <Card className="shadow-xl border-2 border-gray-200">
          <CardHeader className="bg-gradient-to-r from-blue-600 to-orange-500 text-white">
            <CardTitle className="text-2xl">Transportanfrage</CardTitle>
            <CardDescription className="text-blue-50">
              Für eine optimale Planung empfehlen wir eine Buchung 7 Tage im Voraus
            </CardDescription>
          </CardHeader>
          <CardContent className="p-6">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                {/* Personal Info */}
                <div className="space-y-2">
                  <Label htmlFor="name" className="flex items-center space-x-2">
                    <User className="h-4 w-4 text-blue-600" />
                    <span>Ihr Name *</span>
                  </Label>
                  <Input
                    id="name"
                    required
                    value={formData.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    placeholder="Max Mustermann"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="phone" className="flex items-center space-x-2">
                    <Phone className="h-4 w-4 text-blue-600" />
                    <span>Telefonnummer *</span>
                  </Label>
                  <Input
                    id="phone"
                    type="tel"
                    required
                    value={formData.phone}
                    onChange={(e) => handleInputChange('phone', e.target.value)}
                    placeholder="0123 456789"
                  />
                </div>

                <div className="space-y-2 md:col-span-2">
                  <Label htmlFor="email" className="flex items-center space-x-2">
                    <Mail className="h-4 w-4 text-blue-600" />
                    <span>E-Mail</span>
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    placeholder="max@beispiel.de"
                  />
                </div>

                {/* Pet Info */}
                <div className="space-y-2">
                  <Label htmlFor="petType" className="flex items-center space-x-2">
                    <PawPrint className="h-4 w-4 text-orange-600" />
                    <span>Tierart *</span>
                  </Label>
                  <Select value={formData.petType} onValueChange={(value) => handleInputChange('petType', value)}>
                    <SelectTrigger>
                      <SelectValue placeholder="Wählen Sie eine Tierart" />
                    </SelectTrigger>
                    <SelectContent>
                      {petTypes.map((type) => (
                        <SelectItem key={type.value} value={type.value}>
                          {type.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="petName">Name des Tieres</Label>
                  <Input
                    id="petName"
                    value={formData.petName}
                    onChange={(e) => handleInputChange('petName', e.target.value)}
                    placeholder="z.B. Bruno"
                  />
                </div>

                {selectedPetType && (
                  <div className="space-y-2">
                    <Label htmlFor="petSize">Größe *</Label>
                    <Select value={formData.petSize} onValueChange={(value) => handleInputChange('petSize', value)}>
                      <SelectTrigger>
                        <SelectValue placeholder="Wählen Sie die Größe" />
                      </SelectTrigger>
                      <SelectContent>
                        {selectedPetType.sizes.map((size) => (
                          <SelectItem key={size} value={size}>
                            {size}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {/* Trip Type Selection */}
                <div className="space-y-2 md:col-span-2">
                  <Label className="text-base font-semibold">Transportart *</Label>
                  <div className="grid sm:grid-cols-3 gap-3">
                    {[
                      { value: 'short', label: 'Kurzstrecke', desc: '2,20€/km' },
                      { value: 'long', label: 'Langstrecke', desc: '45€/Std' },
                      { value: 'emergency', label: 'Notfall', desc: 'Pauschal' }
                    ].map((type) => (
                      <button
                        key={type.value}
                        type="button"
                        onClick={() => handleInputChange('tripType', type.value)}
                        className={`p-4 rounded-xl border-2 text-left transition-all duration-200 ${
                          formData.tripType === type.value
                            ? 'border-blue-500 bg-blue-50 shadow-md'
                            : 'border-gray-200 hover:border-blue-300 hover:bg-gray-50'
                        }`}
                      >
                        <div className="font-semibold text-gray-900">{type.label}</div>
                        <div className="text-xs text-gray-600 mt-1">{type.desc}</div>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Trip Details */}
                <div className="space-y-2 md:col-span-2">
                  <Label htmlFor="pickupAddress" className="flex items-center space-x-2">
                    <MapPin className="h-4 w-4 text-blue-600" />
                    <span>Abholadresse *</span>
                  </Label>
                  <Input
                    id="pickupAddress"
                    required
                    value={formData.pickupAddress}
                    onChange={(e) => handleInputChange('pickupAddress', e.target.value)}
                    placeholder="Straße, Hausnummer, PLZ Ort"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="destinationType">Zieltyp *</Label>
                  <Select value={formData.destinationType} onValueChange={(value) => handleInputChange('destinationType', value)}>
                    <SelectTrigger>
                      <SelectValue placeholder="Wohin soll es gehen?" />
                    </SelectTrigger>
                    <SelectContent>
                      {destinations.map((dest) => (
                        <SelectItem key={dest.value} value={dest.value}>
                          {dest.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2 md:col-span-2">
                  <Label htmlFor="destination">Zieladresse *</Label>
                  <Input
                    id="destination"
                    required
                    value={formData.destination}
                    onChange={(e) => handleInputChange('destination', e.target.value)}
                    placeholder="Straße, Hausnummer, PLZ Ort"
                  />
                </div>

                {/* Tierschutztarif-Auswahl bei Langstrecke */}
                {formData.tripType === 'long' && (
                  <div className="md:col-span-2 p-5 rounded-xl border-2 border-green-200 bg-gradient-to-br from-green-50 to-emerald-50">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <Label htmlFor="isAnimalWelfare" className="text-base font-semibold cursor-pointer flex items-center gap-2">
                          <span className="text-green-700">Tierschutztarif</span>
                          <span className="text-xs bg-green-600 text-white px-2 py-0.5 rounded-full">Vergünstigt</span>
                        </Label>
                        <p className="text-sm text-gray-700 mt-1">
                          Reduzierter Tarif <strong>0,40 €/km</strong> (statt 45 €/Std) für anerkannte Tierschutzorganisationen
                          und Tierheim-Vermittlungsfahrten. Wird nach Anfrage geprüft.
                        </p>
                      </div>
                      <Switch
                        id="isAnimalWelfare"
                        checked={formData.isAnimalWelfare}
                        onCheckedChange={(checked) => handleInputChange('isAnimalWelfare', checked)}
                        data-testid="booking-animal-welfare-switch"
                      />
                    </div>
                    {formData.isAnimalWelfare && (
                      <div className="mt-4 border-t border-green-200 pt-4 space-y-2">
                        <Label htmlFor="animalWelfareOrg" className="font-semibold text-green-800">
                          Name des Tierschutzvereins / der Organisation *
                        </Label>
                        <Input
                          id="animalWelfareOrg"
                          type="text"
                          value={formData.animalWelfareOrg}
                          onChange={(e) => handleInputChange('animalWelfareOrg', e.target.value)}
                          placeholder="z.B. Tierheim Hagen e.V."
                          required={formData.isAnimalWelfare}
                          className="bg-white"
                          data-testid="booking-animal-welfare-org-input"
                        />
                        <p className="text-xs text-gray-600 italic">
                          Dieser Name erscheint auf der späteren Rechnung. Bitte zur Fahrt einen Nachweis
                          (z.B. Schreiben des Vereins, Vereinsausweis) bereithalten.
                        </p>
                      </div>
                    )}
                  </div>
                )}

                {/* Additional Options for Short Trips */}
                {formData.tripType === 'short' && (
                  <>
                    <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl md:col-span-2">
                      <div>
                        <Label htmlFor="roundTrip" className="text-base font-medium cursor-pointer">
                          Rückfahrt gewünscht
                        </Label>
                        <p className="text-sm text-gray-600">Ich möchte auch die Rückfahrt buchen</p>
                      </div>
                      <Switch
                        id="roundTrip"
                        checked={formData.roundTrip}
                        onCheckedChange={(checked) => handleInputChange('roundTrip', checked)}
                      />
                    </div>

                    <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl md:col-span-2">
                      <div>
                        <Label htmlFor="waitingService" className="text-base font-medium cursor-pointer">
                          Warteservice gewünscht
                        </Label>
                        <p className="text-sm text-gray-600">15€ je angefangene 30 Minuten</p>
                      </div>
                      <Switch
                        id="waitingService"
                        checked={formData.waitingService}
                        onCheckedChange={(checked) => handleInputChange('waitingService', checked)}
                      />
                    </div>

                    {formData.waitingService && (
                      <div className="space-y-2 md:col-span-2">
                        <Label htmlFor="waitingDuration">Voraussichtliche Wartezeit (Minuten)</Label>
                        <Select 
                          value={formData.waitingServiceDuration.toString()} 
                          onValueChange={(value) => handleInputChange('waitingServiceDuration', parseInt(value))}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Wartezeit auswählen" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="30">30 Minuten (15€)</SelectItem>
                            <SelectItem value="60">60 Minuten (30€)</SelectItem>
                            <SelectItem value="90">90 Minuten (45€)</SelectItem>
                            <SelectItem value="120">120 Minuten (60€)</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                  </>
                )}

                {/* Date & Time */}
                <div className="space-y-2">
                  <Label className="flex items-center space-x-2">
                    <CalendarIcon className="h-4 w-4 text-blue-600" />
                    <span>Datum *</span>
                  </Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className="w-full justify-start text-left font-normal"
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {selectedDate ? format(selectedDate, 'PPP', { locale: de }) : 'Datum auswählen'}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={selectedDate}
                        onSelect={setSelectedDate}
                        disabled={(date) => {
                          const sevenDaysFromNow = new Date();
                          sevenDaysFromNow.setDate(sevenDaysFromNow.getDate() + 7);
                          return date < sevenDaysFromNow;
                        }}
                        locale={de}
                      />
                    </PopoverContent>
                  </Popover>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="timeSlot" className="flex items-center space-x-2">
                    <Clock className="h-4 w-4 text-blue-600" />
                    <span>Uhrzeit *</span>
                  </Label>
                  <Select value={formData.timeSlot} onValueChange={(value) => handleInputChange('timeSlot', value)}>
                    <SelectTrigger>
                      <SelectValue placeholder="Zeitfenster wählen" />
                    </SelectTrigger>
                    <SelectContent>
                      {availableTimeSlots.map((slot) => (
                        <SelectItem key={slot} value={slot}>
                          {slot} Uhr
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Notes */}
                <div className="space-y-2 md:col-span-2">
                  <Label htmlFor="notes" className="flex items-center space-x-2">
                    <MessageSquare className="h-4 w-4 text-orange-600" />
                    <span>Besondere Hinweise</span>
                  </Label>
                  <Textarea
                    id="notes"
                    value={formData.notes}
                    onChange={(e) => handleInputChange('notes', e.target.value)}
                    placeholder="z.B. Mein Tier ist ängstlich, benötigt spezielle Transportbox, etc."
                    rows={4}
                  />
                </div>

                {/* AGB und Datenschutz Checkbox */}
                <div className="md:col-span-2">
                  <label className="flex items-start gap-3 cursor-pointer p-4 bg-gray-50 rounded-lg border border-gray-200 hover:bg-gray-100 transition-colors">
                    <input
                      type="checkbox"
                      checked={acceptedTerms}
                      onChange={(e) => setAcceptedTerms(e.target.checked)}
                      className="w-5 h-5 mt-0.5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      data-testid="terms-checkbox"
                    />
                    <span className="text-sm text-gray-700">
                      Ich habe die{' '}
                      <a 
                        href="/agb" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:text-blue-800 underline font-medium"
                      >
                        Allgemeinen Geschäftsbedingungen (AGB)
                      </a>
                      {' '}und die{' '}
                      <a 
                        href="/datenschutz" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:text-blue-800 underline font-medium"
                      >
                        Datenschutzerklärung
                      </a>
                      {' '}gelesen und akzeptiere diese. *
                    </span>
                  </label>
                </div>
              </div>

              {/* Preis-Berechnung (nicht bindend, dient zur Orientierung) */}
              <div className="bg-gradient-to-br from-blue-50 to-orange-50 border-2 border-blue-200 rounded-xl p-5" data-testid="booking-price-section">
                <div className="flex items-start gap-3 mb-4">
                  <div className="bg-blue-100 rounded-full p-2 mt-0.5">
                    <Calculator className="h-5 w-5 text-blue-600" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-gray-900 mb-1">Voraussichtlicher Fahrpreis</h3>
                    <p className="text-sm text-gray-600">
                      Sehen Sie vor dem Absenden, was Ihre Fahrt voraussichtlich kosten wird. Endgültiger Betrag erfolgt nach Streckenprüfung.
                    </p>
                  </div>
                </div>

                <Button
                  type="button"
                  onClick={handleCalculatePrice}
                  disabled={isCalculatingPrice || !formData.pickupAddress || !formData.destination}
                  variant="outline"
                  className="w-full bg-white border-blue-300 hover:bg-blue-50 text-blue-700 font-semibold"
                  data-testid="booking-calculate-price-btn"
                >
                  <Calculator className="h-4 w-4 mr-2" />
                  {isCalculatingPrice ? 'Wird berechnet...' : (estimatedPrice ? 'Preis erneut berechnen' : 'Preis berechnen')}
                </Button>

                {priceError && (
                  <p className="text-sm text-red-600 mt-3 text-center">{priceError}</p>
                )}

                {estimatedPrice && (
                  <div className="mt-4 bg-white rounded-lg p-4 border border-blue-300 shadow-sm" data-testid="booking-estimated-price">
                    <div className="flex items-baseline justify-between mb-2">
                      <span className="text-sm text-gray-600">Voraussichtlicher Preis:</span>
                      <span className="text-3xl font-bold text-blue-600 flex items-center">
                        <Euro className="h-6 w-6 mr-1" />
                        {typeof estimatedPrice.price === 'number' ? estimatedPrice.price.toFixed(2).replace('.', ',') : estimatedPrice.price}
                      </span>
                    </div>
                    {estimatedPrice.details?.routeInfo && (
                      <p className="text-xs text-gray-500 mb-2">{estimatedPrice.details.routeInfo}</p>
                    )}
                    {(estimatedPrice.details?.totalDistance || estimatedPrice.details?.tripDistance) && (
                      <p className="text-xs text-gray-600">
                        Strecke: {estimatedPrice.details.totalDistance || estimatedPrice.details.tripDistance} km
                        {estimatedPrice.details?.totalDurationText && ` · ${estimatedPrice.details.totalDurationText}`}
                      </p>
                    )}
                    <p className="text-xs text-gray-500 mt-3 italic">
                      * Unverbindliche Schätzung. Die endgültige Rechnung kann nach tatsächlicher Strecke/Wartezeit abweichen.
                    </p>
                  </div>
                )}
              </div>

              <Button
                type="submit"
                disabled={isSubmitting || !acceptedTerms}
                className={`w-full h-14 text-lg font-semibold transition-all ${
                  acceptedTerms 
                    ? 'bg-gradient-to-r from-blue-600 to-orange-500 hover:from-blue-700 hover:to-orange-600' 
                    : 'bg-gray-300 cursor-not-allowed'
                }`}
              >
                {isSubmitting ? 'Wird gesendet...' : 'Buchungsanfrage senden'}
              </Button>

              <p className="text-sm text-gray-500 text-center">
                * Pflichtfelder. Nach dem Absenden erhalten Sie eine Bestätigung per Telefon.
              </p>
            </form>
          </CardContent>
        </Card>
      </div>
    </section>
  );
};

export default Booking;
Booking;
